import React from 'react';
import './Podcast.css';

// Econ-on-Mics (IEA partnership) podcast data
const IEA_PODCAST_URL = 'https://www.iea-world.org/podcast/';
const ECON_ON_MICS_SPOTIFY_URL = 'https://open.spotify.com/show/0W200Y1XlQYFlVominBnnf';

const econOnMicsEpisodes = [
  {
    number: 1,
    image: '/ep1.jpg',
    title: 'Climate policy inequality',
    guest: 'Elisa Belfiori',
    description:
      "This episode features Elisa Belfiori discussing the intersections of Climate, Policy, and Inequality. Interviewed by Maria Alsina-Pujols, Belfiori navigates the complexities of studying climate policy, illuminates the current landscape, and discusses the challenges encountered by developing countries in addressing climate change while fostering development.",
  },
  {
    number: 2,
    image: '/ep2.jpg',
    title: "Women's labor market participation",
    guest: 'Kanika Mahajan',
    description:
      "In this episode, Kanika Mahajan discusses her work on gender and labour economics. Interviewed by WiEP Director Anahita Karandikar, she touches upon the stagnation of women's labour force participation in India, exploring both labour supply and demand in light of factors such as social norms and returns to education.",
  },
  {
    number: 3,
    image: '/ep3.jpg',
    title: 'Intergenerational mobility',
    guest: 'Dr. Eleni Yitbarek',
    description:
      "In this episode, Eleni Yitbarek is interviewed by Michelle Pleace about her research on intergenerational mobility in sub-Saharan Africa. The first part of their discussion focuses on the association between social and economic indicators of children and that of their parents. They then delve into comparisons between the mobility of sons and daughters, and the part played by parental bias and women's role in the household.",
  },
  {
    number: 4,
    image: '/ep4.jpg',
    title: "Women's access to decent work",
    guest: 'Dr. Nisha Arunatilake',
    description:
      "Nisha Arunatilake shares her insights on women's access to decent work in Sri Lanka. In conversation with her colleague Lakmini Fernando, she delves into social and legal barriers – including workplace discrimination, maternity leave, and the poor coverage of labour laws – that might restrict women's participation in such work. She discusses how providing access to childcare and public transport, as well as introducing legislature which makes workplaces inclusive of women, can be a step towards facilitating female labour force participation.",
  },
  {
    number: 5,
    image: '/ep5.jpg',
    title: 'Cash transfer and domestic violence',
    guest: 'Dr. Adriana Camacho',
    description:
      "In this episode, Adriana Camacho talks about her work on the Colombian cash transfer program and domestic violence. Interviewed by Santiago de la Cadena, she goes over how a lack of resources could trigger domestic violence, and why the timing, expectation and duration of cash transfers matter when it comes to household behaviours. They discuss how such programs could have positive externalities in terms of investment in human capital, and the need for policymakers to primarily address the issue of scarcity.",
  },
  {
    number: 6,
    image: '/ep6.jpg',
    title: 'Trade and the environment',
    guest: 'Dr. Leila Baghdadi',
    description:
      "In this episode, Leila Baghdadi is joined by Amal Medini to speak about her work on the environmental impact of globalisation, trade and participation in global value chains, as well as her findings on how environmental provisions in trade agreements and tariffs can affect carbon emissions. She also discusses some of her new research on gender equality in the Middle East and North Africa, which explores topics such as the provision of childcare, hiring discrimination against women, and implementing training programs.",
  },
  {
    number: 7,
    image: '/ep7.jpg',
    title: 'Women in economics',
    guest: null,
    description: null,
  },
];

// Behind the Scenes podcast episodes data
const episodes = [
  {
    number: 1,
    image: '/ep1.png',
    title: 'Using Machine Learning & Qualitative Interviews',
    spotifyLink: 'https://open.spotify.com/show/0W200Y1XlQYFlVominBnnf?si=0b5738cd62014ada&nd=1&dlsi=be92afe30e3e4761',
    applePodcastLink: 'https://podcasts.apple.com/us/podcast/econ-on-mics/id1620796827',
    speaker: (
      <>
        Our first guest speaker{' '}
        <a
          href="https://www.qualanalytics.com/team/monica-biradavolu/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Dr. Monica Biradavolu
        </a>
        , Founder and CEO of{' '}
        <a
          href="https://www.qualanalytics.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          QualAnalytics
        </a>{' '}
        and our host{' '}
        <a
          href="https://www.povertyactionlab.org/person/chopra"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ambika Chopra
        </a>
        , Senior Research Associate at{' '}
        <a
          href="https://www.povertyactionlab.org/south-asia"
          target="_blank"
          rel="noopener noreferrer"
        >
          J-PAL South Asia
        </a>{' '}
        discuss an approach which combines mixed-methods data collection and machine learning.
      </>
    ),
    paper: (
      <>
        The host and the speaker discuss the paper{' '}
        <a
          href="https://www.nber.org/system/files/working_papers/w28626/w28626.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          'Using machine learning and qualitative interviews to design a five-question women's agency
          index'
        </a>
        .
      </>
    ),
    quote: (
      <>
        "What seems like the perfect example of agency to one woman might not be the same for another.
        It's important to acknowledge such subjectivities and caveats in surveys when gathering
        pre-planned data."
      </>
    ),
    body: (
      <>
        Dr. Monica, on the basis of her field experiences, also talks about the scope of using ML in
        designing such short surveys. Tune in now for a riveting interview-cum-conversation!
      </>
    ),
  },
  {
    number: 2,
    image: '/ep2.png',
    title: 'Sex Workers, Stigma, and Self-Image: Evidence from Kolkata Brothels',
    spotifyLink: 'https://open.spotify.com/show/0W200Y1XlQYFlVominBnnf?si=0b5738cd62014ada&nd=1&dlsi=0f4dc115030a451f',
    applePodcastLink: 'https://podcasts.apple.com/us/podcast/econ-on-mics/id1620796827',
    speaker: (
      <>
        Guest speaker{' '}
        <a
          href="https://www.bsg.ox.ac.uk/people/anandi-mani"
          target="_blank"
          rel="noopener noreferrer"
        >
          Anandi Mani
        </a>{' '}
        walks us through her paper{' '}
        <a
          href="https://anandim.github.io/webpage/sexworkers_restat_web.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Sex Workers, Stigma, and Self-Image
        </a>{' '}
        along with host Avantika Prabhakar, PhD candidate in Economics at the University of
        Virginia.
      </>
    ),
    body: (
      <>
        The episode focuses on a paper studying the link between self-image and behavior for people
        facing stigma due to poverty and exclusion. The research, collaborated with{' '}
        <a
          href="https://www.ideasforindia.in/profile/smarajit.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          Dr. Smarajit Jana
        </a>{' '}
        and{' '}
        <a href="https://durbar.org/" target="_blank" rel="noopener noreferrer">
          Durbar
        </a>
        , included a randomized field experiment with Kolkata sex workers. Findings: Psychological
        interventions improved self-image, savings choices, and clinic visits. This episode explores
        the potential for psychological interventions to improve outcomes among marginalized groups.
      </>
    ),
  },
  {
    number: 3,
    image: '/ep3.png',
    title: 'Curse of the Mummyji - Influence of Mothers-in-law on Women in India',
    spotifyLink: 'https://open.spotify.com/show/0W200Y1XlQYFlVominBnnf?si=0b5738cd62014ada&nd=1&dlsi=09ae6ae963ec482e',
    applePodcastLink: 'https://podcasts.apple.com/us/podcast/econ-on-mics/id1620796827',
    speaker: (
      <>
        <a
          href="https://sites.google.com/view/s-anukriti/home"
          target="_blank"
          rel="noopener noreferrer"
        >
          S. Anukrit
        </a>
        , Research Economist at The World Bank, unpacks complexities of restrictive norms and
        women's social networks, with host{' '}
        <a
          href="https://www.linkedin.com/in/sakshi-hallan-093492b3/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Sakshi Hallan
        </a>
        , Research Analyst at The World Bank.
      </>
    ),
    paper: (
      <>
        They discuss{' '}
        <a
          href="https://openknowledge.worldbank.org/handle/10986/37002"
          target="_blank"
          rel="noopener noreferrer"
        >
          Curse of the Mummy-ji: the influence of Mothers-in-law on Women in India
        </a>
        .
      </>
    ),
    body: (
      <>
        Using primary data from rural Uttar Pradesh, the study shows how co-residence with a
        mother-in-law affects a daughter-in-law's access to resources and social connections. <br />
        <em>
          "It could be that you have a well-intentioned programme but it overlooks how the
          mother-in-law will either perceive that program or prevent the daughter-in-law from
          accessing it or maybe she is actually going to be a supportive influence."
        </em>
      </>
    ),
  },
  {
    number: 4,
    image: '/ep4.png',
    title:
      "On Her Own Account - How Strengthening Women's Financial Control Affects Labor Supply and Gender Norms",
    spotifyLink: 'https://open.spotify.com/show/0W200Y1XlQYFlVominBnnf?si=0b5738cd62014ada&nd=1&dlsi=d36829bddaaf4e22',
    applePodcastLink: 'https://podcasts.apple.com/us/podcast/econ-on-mics/id1620796827',
    speaker: (
      <>
        Dr.{' '}
        <a
          href="https://macmillan.yale.edu/people/charity-troyer-moore"
          target="_blank"
          rel="noopener noreferrer"
        >
          Charity Troyer Moore
        </a>
        , Director for South Asia Economics Research at Yale University's MacMillan Center along
        with{' '}
        <a
          href="https://www.linkedin.com/in/shambhavisawhney/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Shambhavi Sawhney
        </a>
        , Data Associate at{' '}
        <a
          href="https://www.goodbusinesslab.org/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Good Business Lab
        </a>
        .
      </>
    ),
    paper: (
      <>
        They revisit{' '}
        <a
          href="https://www.nber.org/system/files/working_papers/w26294/w26294.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          On her Own Account: How Strengthening Women's Financial Control Affects Labour Supply and
          Gender Norms
        </a>
        , co-authored by{' '}
        <a
          href="https://campuspress.yale.edu/rpande/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Dr. Rohini Pande
        </a>
        ,{' '}
        <a
          href="https://sites.google.com/site/sschaner/home"
          target="_blank"
          rel="noopener noreferrer"
        >
          Dr. Simone Schaner
        </a>{' '}
        and{' '}
        <a
          href="https://sites.google.com/view/nataliarigol"
          target="_blank"
          rel="noopener noreferrer"
        >
          Dr. Natalia Rigol
        </a>
        .
      </>
    ),
    body: (
      <>
        The team explored how greater control over earned income incentivises women to work and
        influences gender norms. Dr. Charity recounts her experience in Delhi, field work,
        bureaucratic hurdles, and discusses survey design's effect on data. She concludes: access to
        economic opportunities leads to more bargaining power for women, which is key for spousal
        discordance issues.
      </>
    ),
  },
  {
    number: 5,
    image: '/ep5.png',
    title: 'Sleepless In ... Chennai',
    spotifyLink: 'https://open.spotify.com/episode/0YmlwA4QvTTlpvcbhKv2xx?si=438310adad7a48fe&nd=1&dlsi=5aed87b4dd9347ab',
    applePodcastLink: 'https://podcasts.apple.com/us/podcast/econ-on-mics/id1620796827',
    speaker: (
      <>
        <a
          href="https://heatherschofield.net/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Dr. Heather Schofield
        </a>
        , Assistant Professor at Perelman School of Medicine and Wharton, and{' '}
        <a
          href="https://www.linkedin.com/in/vasanthi-s-pillai/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Vasanthi Pillai
        </a>
        , PhD researcher at Technical University of Munich.
      </>
    ),
    body: (
      <>
        Dr. Heather covers research on sleep, Ramadan fasting, agricultural production, loneliness,
        cognitive endurance, and describes co-founding the{' '}
        <a
          href="https://behavioraldevlab.org/about/about.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          Behavioral Development Lab
        </a>{' '}
        in Chennai. She discusses{' '}
        <a
          href="https://economics.mit.edu/files/16994"
          target="_blank"
          rel="noopener noreferrer"
        >
          The Economic Consequences of Increasing Sleep Among the Urban Poor
        </a>{' '}
        (using actigraphy), and{' '}
        <a
          href="https://haushofer.ne.su.se/ec2303/Lecture%205%20-%20Nutrition/Schofield_WP_2020.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Ramadan Fasting and Agricultural Output
        </a>
        . Dr. Heather offers valuable advice for researchers and talks about how religious influence
        is rarely analyzed in labor policies.
      </>
    ),
  },
];

function Podcast() {
  return (
    <div className="podcast__container">
      <div className="podcast__bts">
        <div className="podcast__bts-left">
          <h1>
            Behind <br /> the <br /> Scenes
          </h1>
        </div>
        <div className="podcast__bts-right">
          <img
            src="/bts.png"
            alt="BTS Podcast Cover"
            className="podcast__bts-img"
          />
          <div className="podcast__bts-content">
            <h2>Economics Research</h2>
            <p className="podcast__bts-team">
              Economics Research
              <br />
              BTS Production Team
              <br />
              Ria Dutta, Sakshi Hallan, Samhita Kuntia, Fizza Suhel, Prerna
              Kundu, &amp; Ridhi Aggarwal
            </p>
            <p style={{ marginTop: '1rem' }}>
              <a
                className="podcast__link"
                href="https://cobalt-baker-732.notion.site/Behind-the-Scenes-of-Econ-Research-A-podcast-on-research-by-women-Episode-Guide-007ca4f6e6f8403a892a9ed329d49f3f"
                target="_blank"
                rel="noopener noreferrer"
              >
                Episode Guide ↗
              </a>
              <br />
              <a
                className="podcast__link"
                href="https://cobalt-baker-732.notion.site/Behind-the-Scenes-of-Econ-Research-A-podcast-on-research-by-women-Episode-Guide-007ca4f6e6f8403a892a9ed329d49f3f"
                target="_blank"
                rel="noopener noreferrer"
              >
                Show Notes and Resources ↗
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className="podcast__episodes">
        {episodes.map((ep) => (
          <div className="podcast__episode-card" key={ep.number}>
            <div className="podcast__ep-label">{`EP ${ep.number}`}</div>

            {/* Image + overlayed text */}
            <div className="podcast__episode-img-wrapper">
              <img
                src={ep.image}
                alt={`Episode ${ep.number}`}
                className="podcast__episode-img"
              />
              <div className="podcast__episode-gradient" />

              <div className="podcast__episode-overlay">
                <div className="podcast__episode-text-above">
                  <h3>{ep.title}</h3>
                  
                </div>
              </div>
            </div>

            {/* Podcast Platform Links */}
            <div className="podcast__episode-links">
              <a
                href={ep.spotifyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="podcast__platform-button podcast__spotify-button"
              >
                <img src="/spotify.png" alt="Spotify" />
                <span>Spotify</span>
              </a>
              <a
                href={ep.applePodcastLink}
                target="_blank"
                rel="noopener noreferrer"
                className="podcast__platform-button podcast__apple-button"
              >
                <img src="/applePodcast.png" alt="Apple Podcasts" />
                <span>Apple</span>
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Econ-on-Mics (IEA) series */}
      <div className="podcast__eom">
        <div className="podcast__eom-left">
          <h1>
            Econ on <br /> Mics
          </h1>
        </div>
        <div className="podcast__eom-right">
          <div className="podcast__eom-content">
            <h2>Econ-on-Mics Podcast</h2>
            <p className="podcast__eom-desc">
              A joint production of the International Economic Association's Women in Leadership in Economics initiative and Women in Econ/Policy.
            </p>
            <p style={{ marginTop: '1rem' }}>
              <a
                className="podcast__link"
                href={IEA_PODCAST_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                IEA Podcast Page ↗
              </a>
              <br />
              <a
                className="podcast__link"
                href={ECON_ON_MICS_SPOTIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Listen on Spotify ↗
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className="podcast__episodes podcast__episodes-eom">
        {econOnMicsEpisodes.map((ep) => (
          <div className="podcast__episode-card" key={`eom-${ep.number}`}>
            <div className="podcast__ep-label">{`EP ${ep.number}`}</div>

            <div className="podcast__episode-img-wrapper">
              <img
                src={ep.image}
                alt={`Econ-on-Mics Episode ${ep.number}`}
                className="podcast__episode-img"
              />
              <div className="podcast__episode-gradient" />
              <div className="podcast__episode-overlay">
                <div className="podcast__episode-text-above">
                  <h3>{ep.title}</h3>
                  {ep.guest && <p className="podcast__ep-guest">by {ep.guest}</p>}
                </div>
              </div>
            </div>

            <div className="podcast__episode-links">
              <a
                href={ECON_ON_MICS_SPOTIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="podcast__platform-button podcast__spotify-button"
              >
                <img src="/spotify.png" alt="Spotify" />
                <span>Spotify</span>
              </a>
              <a
                href={IEA_PODCAST_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="podcast__platform-button podcast__apple-button"
              >
                <span>IEA</span>
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* <div className="mentorship__partners-section">
        <h2>Our Partners</h2>
        <p className="mentorship__partners-desc">
          Our clients are leading organizations, universities, non-profits and
          social enterprises who tackle the toughest challenges of our time.
        </p>
        <div className="mentorship__partners-logos">
          <img src="/logo.png" alt="WWF Logo" />
          <img src="/mentorshiplogo1.png" alt="World Economic Forum Logo" />
          <img src="/mentorshiplogo2.png" alt="Teach For Canada Logo" />
          <img src="/mentorshiplogo3.png" alt="Oxfam Canada Logo" />
          <img src="/mentorshiplogo4.png" alt="Fair Food Network Logo" />
          <img src="/mentorshiplogo5.png" alt="United Nations Logo" />
          <img src="/mentorship6.png" alt="Gairdner Logo" />
        </div>
      </div> */}
    </div>
  );
}

export default Podcast;
